import type { MomentInput } from 'moment';
import moment from 'moment';

const DATETIME_FORMAT = 'D MMM YYYY, HH:mm[Z]';
const DATETIME_FORMAT_V2 = 'DD HHmm[Z] MMM YYYY';
const DATETIME_FORMAT_V2_WITHOUT_TIME = 'DD MMM YYYY';
const DATETIME_FORMAT_V2_WITH_SECONDS = 'DD HHmmss[Z] MMM YYYY';
const DATE_INPUT_FORMAT = 'MM/DD/YYYY';
const TIME_INPUT_FORMAT = 'HH:mm';
const TIME_INPUT_FORMATS = ['HH:mm', 'Hmm', 'HHmm'];
const PRETTY_DATE_FORMAT = 'MMM DD, YYYY';

const INTERVALS = { hours: 60 * 60 * 1000, days: 24 * 60 * 60 * 1000 };

interface LegacyDuration {
  days?: number;
  hours?: number;
}

/**
 * @deprecated
 * @param interval
 * @returns
 */
function fromLegacyInterval(interval: LegacyDuration | null | undefined): number {
  if (!interval) return 0;
  let duration = 0;
  if (interval.days) duration += interval.days * INTERVALS.days;
  if (interval.hours) duration += interval.hours * INTERVALS.hours;
  return duration;
}

/**
 * @deprecated
 * @param duration
 * @returns
 */
function toLegacyInterval(duration: number | null | undefined): LegacyDuration {
  if (!duration) return { days: 0, hours: 0 };
  const days = Math.floor(duration / INTERVALS.days);
  const hours = Math.floor((duration - days * INTERVALS.days) / INTERVALS.hours);
  return { days, hours };
}

function midnight(): Date {
  return new Date(new Date().setHours(0, 0, 0, 0));
}

function formatDateTime(date: MomentInput): string {
  return moment(date).format(DATETIME_FORMAT);
}

function parseISODate(value: string | Date): Date {
  return moment.utc(value).toDate();
}

// TODO super confusing param name; datesArray is not Date[], it's an array of objects with a property of [attrName] which is a Date, i.e. { updatedAt: new Date() }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function sortDatesDescending(datesArray: any, attrName: string): any {
  return datesArray.sort((a, b) => {
    if (a[attrName] > b[attrName]) return -1;
    if (a[attrName] < b[attrName]) return 1;
    return 0;
  });
}

const getDaysSinceEpoch = (date: Date | string): number => {
  const dateValue = moment(date);

  return Math.floor(parseInt(dateValue.format('X'), 10) / 86400);
};

// Regex that tests for optional leading zeros in month and day and either a 2 or 4 digit year
const mmddyyyyRegex = /^(0?[1-9]|1[012])\/(0?[1-9]|[12][0-9]|3[01])\/(\d{2}|\d{4})$/;

// Regex that tests for optional leading zeros in month and day with no year
const mmddRegex = /^(0?[1-9]|1[012])\/(0?[1-9]|[12][0-9]|3[01])$/;

// Regex that tests a time string in the format HH:mm/H:mm and is between 00:00 and 23:59
const hhmmRegex = /^([0-9]|[01]\d|2[0-3]):?([0-5]\d)$/;

// Intl Formatters
interface DateTimeFormatter {
  datePart: string;
  timePart: string;
  dayPeriod: string | undefined;
}

const defaultDateAndTimeFormatter = (date: Date, tz: string): DateTimeFormatter => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);

  const datePart = [
    parts.find((p) => p.type === 'month')?.value,
    parts.find((p) => p.type === 'day')?.value,
    parts.find((p) => p.type === 'year')?.value,
  ].join('/');

  const timePart = [
    parts.find((p) => p.type === 'hour')?.value,
    parts.find((p) => p.type === 'minute')?.value,
  ].join(':');

  const dayPeriod = parts.find((p) => p.type === 'dayPeriod')?.value;

  return { datePart, timePart, dayPeriod };
};

// Intl helpers
const addYear = (date: Date, years: number): Date => {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() + years);
  return d;
};

function getTimeZone(locale: string): string {
  const parts = new Intl.DateTimeFormat(locale, {
    timeZoneName: 'short',
  }).formatToParts(new Date());

  const tzPart = parts.find((p) => p.type === 'timeZoneName');
  return tzPart?.value ?? '';
}

export {
  fromLegacyInterval,
  toLegacyInterval,
  INTERVALS,
  midnight,
  formatDateTime,
  parseISODate,
  sortDatesDescending,
  getDaysSinceEpoch,
  DATETIME_FORMAT,
  DATETIME_FORMAT_V2,
  DATETIME_FORMAT_V2_WITHOUT_TIME,
  DATETIME_FORMAT_V2_WITH_SECONDS,
  DATE_INPUT_FORMAT,
  PRETTY_DATE_FORMAT,
  TIME_INPUT_FORMAT,
  TIME_INPUT_FORMATS,
  mmddyyyyRegex,
  mmddRegex,
  hhmmRegex,
  addYear,
  defaultDateAndTimeFormatter,
  getTimeZone,
};
